/**
 * Boot: fonts → asset manifest → services → mount the DOM overlay → Title. (ARCHITECTURE §8 "Boot")
 *
 * Composition root. It calls exactly ONE factory per folder and never needs editing to swap an implementation:
 *   render       createMatchRenderer      src/render/index.ts   (loaded lazily: Pixi is its own chunk)
 *   audio        createClientAudioEngine  src/audio-bridge/engine.ts
 *                createAudioBridge        src/audio-bridge/index.ts
 *   input        createInputManager       src/input/manager.ts
 *   online       createOnlineClient       src/net/index.ts
 */
import { render, h } from 'preact';
// The HUD, moments and audio cues already pull the sim's enums/events into the first bundle; the probe is tiny.
import { runDeterminismProbe } from '@hf/sim';
import { createAudioBridge } from '@hf/client-core/audio-bridge';
import { createClientAudioEngine } from '../audio-bridge/engine';
import { createInputManager } from '../input/manager';
import { createOnlineClient } from '../net/browser-env';
import { App } from '../ui/App';
import { pendingMatch } from '@hf/client-core/ui-model/flow';
import type { UiServices } from '../ui/actions';
import { ui } from '@hf/client-core/ui-model/store';
import { loadManifest, type AssetManifest } from '@hf/render-core/assets/manifest';
import { lazyRendererFactory, whenIdle } from './lazy-renderer';
import { LazyMatchScene } from './lazy-match-scene';
import type { MatchSceneDeps } from './match-scene';
import { OnlineFlow } from '@hf/client-core/match/online-flow';
import { ReplayLibrary } from '@hf/client-core/replay/library';
import { resultsFromState } from '@hf/client-core/match/results';
import { SceneManager } from '@hf/client-core/match/scene-manager';
import { createAppController } from '@hf/client-core/ui-model/app-controller';

import { initAnalytics, installAnalyticsTracking, instrumentOnline } from '@hf/client-core/analytics';
import { createWebDriver } from '../analytics/posthog-driver';
import { campaignFromUrl, webAnalyticsConfig } from '../analytics/config';
import { MANIFEST_URL, PORTAL } from '../portal';

export interface BootTargets {
  stage: HTMLElement;
  ui: HTMLElement;
}

export interface AppContext {
  manifest: AssetManifest;
  scenes: SceneManager;
  services: UiServices;
}

/** Debug/e2e hooks on `window.__HF__`. */
export interface HfDebugHooks {
  ui: typeof ui;
  scenes: SceneManager;
  manifest: AssetManifest;
  /** Tick of the running match, -1 outside a match. */
  matchTick(): number;
  /**
   * Why an online match lost its peer (e2e failure snapshots): the rollback session's `disconnectReason`
   * (timeout = no packets within the disconnect timeout, transport_closed, version, quit, left…), the server's
   * `opponent_left` reason, and the live transport. Nulls when nothing happened.
   */
  netDebug(): { disconnectReason: string | null; opponentLeft: string | null; transport: string | null };
  /** Run @hf/sim's determinism probe in this runtime (e2e compares it with Node). */
  simProbe(checkpoints?: readonly number[]): number[];
  /**
   * Last CONFIRMED tick of the running match (inputs final on both peers; offline = every tick), -1 when none.
   * Online e2e compares peers on the confirmed timeline only (`current` may be a prediction).
   */
  confirmedTick(): number;
  /**
   * `hashState` of the confirmed shadow state and its tick (`tick` = confirmed steps taken, i.e.
   * `confirmedTick() + 1`); null outside a recorded match (practice / tutorial have no confirmed timeline).
   */
  confirmedHash(): { tick: number; hash: number } | null;
  /** Alias of `confirmedHash()` (the latest one). */
  lastConfirmedHash(): { tick: number; hash: number } | null;
  /** Ids of the instant-replay clips recorded in the current / last match (best first). */
  replayClips(): number[];
}

const loadFonts = async (): Promise<void> => {
  if (!('fonts' in document)) return;
  const fonts = ['48px "Luckiest Guy"', '24px "Bungee"', '400 16px "Fredoka"', '700 16px "Fredoka"'];
  const all = Promise.all(fonts.map((f) => document.fonts.load(f))).then(() => undefined);
  // Never block boot on a slow font: fall back after 2.5 s.
  await Promise.race([all, new Promise<void>((r) => setTimeout(r, 2500))]);
};

export const boot = async (targets: BootTargets): Promise<AppContext> => {
  const [manifest] = await Promise.all([loadManifest(MANIFEST_URL), loadFonts()]);

  const audioEngine = createClientAudioEngine();
  audioEngine.setVolumes(ui.settings.value.volumes);
  const audioBridge = createAudioBridge(audioEngine, ui);
  // Analytics: OFF until the player opts in (first-run prompt / Settings). No gameplay code is touched: events are
  // derived from the UI store (client-core analytics/tracker.ts).
  const ac = webAnalyticsConfig();
  // Portal build: no analytics at all (offline static bundle, no consent prompt).
  if (!PORTAL)
    initAnalytics(
      {
        driver: createWebDriver({ key: ac.key, host: ac.host }),
        platform: 'web',
        appVersion: ac.version,
        buildNumber: ac.build,
        otaUpdateId: 'embedded',
        dev: ac.dev,
      },
      campaignFromUrl(),
    );
  if (!PORTAL) installAnalyticsTracking(ui);
  const services: UiServices = {
    input: createInputManager(),
    online: instrumentOnline(createOnlineClient(ui), ui),
    audio: audioEngine,
  };
  const scenes = new SceneManager(ui, audioBridge);
  // Pixi + the stadium renderer are a separate chunk: fetched when idle after the first paint, at the latest when
  // the first renderer mounts (the Versus splash prepares it).
  const loadRenderer = () => import('../render').then((m) => m.createMatchRenderer);
  const createRenderer = lazyRendererFactory(loadRenderer);
  whenIdle(() => void loadRenderer().catch(() => undefined));
  const replays = new ReplayLibrary({ ui, manifest, createRenderer, audio: audioBridge });
  services.replays = replays;
  const onlineFlow = new OnlineFlow(ui, services.online, {
    goto: (screen) => void scenes.goto(screen),
    startMatch: (setup) => actions.startMatch(setup),
  });
  // The match scene (sim, CPU, netcode, replays) is a separate chunk too: fetched when idle, or by the Versus splash.
  const matchDeps: MatchSceneDeps = {
    ui,
    stage: targets.stage,
    manifest,
    input: services.input,
    audio: audioBridge,
    createRenderer,
    replays,
    onMatchEnd: (setup, final) => {
      ui.results.value = resultsFromState(setup, final);
      void scenes.goto('results');
    },
    online: onlineFlow,
    onAbort: () => void scenes.goto('online_lobby'),
  };
  const matchScene = new LazyMatchScene(() =>
    import('./match-scene').then((m) => new m.MatchScene(matchDeps)),
  );
  whenIdle(() => matchScene.preload(), 4000);
  scenes.register(matchScene);
  // UiActions + the app effects (bindings follow settings, Versus warms the renderer, Results closes the theater):
  // @hf/client-core's app controller, shared with the native shell.
  const { actions } = createAppController({
    ui,
    goto: (screen) => void scenes.goto(screen),
    online: services.online,
    onlineFlow,
    matchScene,
    pendingSetup: () => pendingMatch.value?.setup ?? null,
    replays,
    input: services.input,
    unlockAudio: () => void audioEngine.unlock(),
  });

  const confirmedHash = (): { tick: number; hash: number } | null => {
    const t = matchScene.confirmed;
    return t ? { tick: t.state.tick, hash: t.hash() } : null;
  };

  render(h(App, { actions, services }), targets.ui);
  await scenes.goto('title');
  const hooks: HfDebugHooks = {
    ui,
    scenes,
    manifest,
    matchTick: () => matchScene.tick,
    netDebug: () => {
      const d = matchScene.currentDriver as { disconnectReason?: string | null } | null;
      return {
        disconnectReason: d?.disconnectReason ?? null,
        opponentLeft: onlineFlow.lastOpponentLeft,
        transport: ui.net.value.transport ?? null,
      };
    },
    simProbe: (checkpoints) => runDeterminismProbe(checkpoints),
    confirmedTick: () => matchScene.confirmed?.confirmedTick ?? -1,
    confirmedHash,
    lastConfirmedHash: confirmedHash,
    replayClips: () => replays.clips().map((c) => c.id),
  };
  (window as unknown as { __HF__?: HfDebugHooks }).__HF__ = hooks;
  return { manifest, scenes, services };
};
